import type * as z from "zod";
import type { approvePlan as approvePlanTool } from "../../api/ai/tools/approvePlan";
import { ChatActionPrompt } from "../components/Chat/ChatActionPrompt";
import type { ChatTool, ToolUseProps } from "../components/Chat/ChatTool";
import { ToolStatus } from "../components/Chat/ToolStatus";
import { Text } from "../components/Text";

type Input = z.infer<ReturnType<typeof approvePlanTool>["inputSchema"]>;
type UnparsedInput = { [K in keyof Input]?: unknown };

type Output = {
  approved?: unknown;
};

export function parseApprovePlanInput(input: unknown): Input | null {
  if (!input || typeof input !== "object" || Array.isArray(input)) return null;
  const { title, plan } = input as UnparsedInput;
  if (typeof title !== "string" || typeof plan !== "string") return null;
  return { title, plan };
}

function parseOutput(output: unknown): { approved: boolean } | null {
  if (!output || typeof output !== "object" || Array.isArray(output))
    return null;
  const approved = (output as Output).approved;
  return typeof approved === "boolean" ? { approved } : null;
}

function ApprovePlanHeader({ toolUse, result, interrupted }: ToolUseProps) {
  const input = parseApprovePlanInput(toolUse.input);
  if (!result || result.isError) {
    return (
      <ToolStatus
        icon="list-todo"
        text={input ? `Plan ready: ${input.title}` : "Plan ready"}
        pending={!interrupted}
      />
    );
  }

  const output = parseOutput(result.output);
  if (!output?.approved) {
    return <ToolStatus icon="circle-minus" text="Plan needs more work" />;
  }

  return (
    <ToolStatus
      icon="check-filled"
      iconColor="var(--green-600)"
      text={input ? `Plan approved: ${input.title}` : "Plan approved"}
    />
  );
}

function ApprovePlanBody({ toolUse, result }: ToolUseProps) {
  const input = parseApprovePlanInput(toolUse.input);
  const output = result ? parseOutput(result.output) : null;
  if (!input) return null;

  return (
    <div style={{ padding: "2px 6px 8px 6px" }}>
      {output?.approved && (
        <Text variant="text-smaller-md" color="var(--text-tertiary)">
          Approved
        </Text>
      )}
    </div>
  );
}

export const approvePlan: ChatTool = {
  name: "approvePlan",
  Header: ApprovePlanHeader,
  Body: ApprovePlanBody,
  alwaysLoadOutput: true,
  PendingPrompt: ({ input, onResult }) => {
    const parsed = parseApprovePlanInput(input);
    if (!parsed) return null;
    return (
      <ChatActionPrompt
        title="What would you like to do?"
        notification="Approve the plan"
        confirmLabel="Approve and build"
        confirmIcon="check"
        cancelLabel="Keep planning"
        cancelIcon="pencil"
        onConfirm={() =>
          onResult({ approved: true }, false, {
            sendStagedAttachments: true,
            approvedPlan: true,
          })
        }
        onCancel={() =>
          onResult({ approved: false }, false, { pauseAfter: true })
        }
      />
    );
  },
};
